import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LifeBuoy, Search, Unlock, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUBRIQUES = [
  { id: "lago", nom: "Lago" },
  { id: "love", nom: "Love" },
  { id: "vip", nom: "VIP" },
  { id: "air", nom: "Air-Bengue" },
  { id: "tout", nom: "Pack Tout-Accès" },
];
const DUREES = [
  { id: "decouverte", nom: "24 heures" },
  { id: "week", nom: "1 semaine" },
  { id: "month", nom: "1 mois" },
];

const Ligne = ({ libelle, valeur }) => (
  <div className="flex justify-between gap-3 text-sm py-1.5 border-b border-[#EFE9DF] last:border-0">
    <span className="text-stone-500">{libelle}</span>
    <span className="font-semibold text-stone-900 text-right">{valeur}</span>
  </div>
);

/**
 * Service après-vente : retrouve un client par numéro / référence de paiement, explique
 * pourquoi son accès est fermé, et permet de l'ouvrir immédiatement (journalisé).
 */
export const DeblocageClientSection = ({ adminId }) => {
  const [q, setQ] = useState("");
  const [diag, setDiag] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [rubrique, setRubrique] = useState("air");
  const [duree, setDuree] = useState("decouverte");
  const [motif, setMotif] = useState("");

  const rechercher = async (valeur = q) => {
    if (!valeur.trim()) return toast.error("Entrez un numéro ou une référence de paiement.");
    setChargement(true);
    try {
      const r = await axios.get(`${API}/admin/acces/diagnostic`, {
        params: { q: valeur.trim(), admin_id: adminId },
      });
      setDiag(r.data);
      const der = r.data.paiements?.[0];
      if (der?.category) setRubrique(der.category);
      if (der?.period) setDuree(der.period);
    } catch (e) {
      setDiag(null);
      toast.error(e.response?.data?.detail || "Recherche impossible.");
    } finally {
      setChargement(false);
    }
  };

  const ouvrirAcces = async () => {
    setChargement(true);
    try {
      const r = await axios.post(`${API}/admin/acces/ouvrir`, {
        admin_id: adminId, q: q.trim(), category: rubrique, period: duree, motif,
      });
      toast.success(`Accès ouvert pour ${r.data.membre.firstname || r.data.membre.contact}.`,
        { id: "deblocage-ouvrir" });
      await rechercher();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ouverture impossible.");
    } finally {
      setChargement(false);
    }
  };

  const reconcilier = async () => {
    setChargement(true);
    try {
      const r = await axios.post(`${API}/admin/paiements/reconcilier`, { admin_id: adminId });
      if (r.data.erreur) {
        toast.error(`CinetPay injoignable : ${r.data.erreur}`);
      } else {
        toast.success(
          `${r.data.verifies} paiement(s) vérifié(s) · ${r.data.abonnements_ouverts} accès ouvert(s) · ${r.data.annonces_publiees} annonce(s) publiée(s).`
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réconciliation impossible.");
    } finally {
      setChargement(false);
    }
  };

  return (
    <section className="mt-14" data-testid="deblocage-client-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2.5">
          <LifeBuoy size={24} className="text-[#E05638]" /> Débloquer un client
        </h2>
        <button onClick={reconcilier} disabled={chargement} data-testid="deblocage-reconcilier"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-stone-900 border border-[#E5DFD5] bg-white rounded-full px-3 py-1.5 disabled:opacity-50">
          <RefreshCw size={13} className={chargement ? "animate-spin" : ""} /> Rattraper les paiements CinetPay
        </button>
      </div>
      <p className="mt-2 text-sm text-stone-500">
        Numéro du client (ex. +2250799220242), identifiant de profil ou référence de paiement (ABNCA-…).
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && rechercher()}
          placeholder="+2250799220242 ou ABNCA-820ce77a-1790972806"
          data-testid="deblocage-recherche-input"
          className="flex-1 min-w-[260px] rounded-full border border-[#E5DFD5] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#E05638]"
        />
        <button onClick={() => rechercher()} disabled={chargement} data-testid="deblocage-rechercher-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-stone-700 disabled:opacity-50">
          <Search size={14} /> Diagnostiquer
        </button>
      </div>

      {diag && (
        <div className="mt-5 rounded-2xl border border-[#E5DFD5] bg-white p-5" data-testid="deblocage-diagnostic">
          <p className="rounded-xl bg-[#FAF8F5] px-4 py-3 text-sm font-semibold text-stone-800" data-testid="deblocage-verdict">
            {diag.verdict}
          </p>
          {diag.passerelle_injoignable && (
            <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800"
              data-testid="deblocage-passerelle-ko">
              Passerelle CinetPay injoignable : {diag.passerelle_injoignable}. Les paiements ne
              peuvent plus être confirmés automatiquement — vérifiez la clé API CinetPay et le
              proxy à IP fixe. En attendant, ouvrez l'accès manuellement ci-dessous.
            </p>
          )}
          <div className="mt-4">
            <Ligne libelle="Membre" valeur={`${diag.membre.firstname || "—"} · ${diag.membre.contact}`} />
            <Ligne libelle="Rubrique d'inscription" valeur={(diag.membre.category || "—").toUpperCase()} />
            <Ligne
              libelle="Numéros débloqués"
              valeur={diag.numeros_debloques ? "Oui" : "Non"}
            />
            <Ligne libelle="Photos en clair" valeur="Oui (pour tout le monde)" />
            <Ligne
              libelle="Abonnements actifs"
              valeur={
                diag.abonnements.length
                  ? diag.abonnements
                      .map((a) => `${a.category.toUpperCase()} jusqu'au ${a.expires_at.slice(0, 16).replace("T", " ")}`)
                      .join(" · ")
                  : "Aucun"
              }
            />
          </div>

          {diag.paiements.length > 0 && (
            <div className="mt-4" data-testid="deblocage-paiements">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-400">Derniers paiements</h3>
              <div className="mt-2 space-y-1.5">
                {diag.paiements.slice(0, 6).map((p) => (
                  <div key={p.transaction_id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
                    <span className="font-mono text-stone-800">{p.transaction_id}</span>
                    <span>{p.category?.toUpperCase()} · {p.montant?.toLocaleString("fr-FR")} FCFA</span>
                    <span className={`rounded-full border px-2 py-0.5 font-semibold ${
                      p.statut === "accepte"
                        ? "border-[#0A5C36]/20 bg-[#0A5C36]/10 text-[#0A5C36]"
                        : "border-amber-200 bg-amber-100 text-amber-800"
                    }`}>
                      {p.statut}
                    </span>
                    {p.statut_cinetpay && (
                      <span className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5">
                        CinetPay : {p.statut_cinetpay}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[#EFE9DF] pt-4">
            <select value={rubrique} onChange={(e) => setRubrique(e.target.value)} data-testid="deblocage-rubrique-select"
              className="rounded-full border border-[#E5DFD5] bg-white px-4 py-2 text-sm">
              {RUBRIQUES.map((r) => <option key={r.id} value={r.id}>{r.nom}</option>)}
            </select>
            <select value={duree} onChange={(e) => setDuree(e.target.value)} data-testid="deblocage-duree-select"
              className="rounded-full border border-[#E5DFD5] bg-white px-4 py-2 text-sm">
              {DUREES.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
            </select>
            <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (ex. paiement encaissé)"
              data-testid="deblocage-motif-input"
              className="flex-1 min-w-[180px] rounded-full border border-[#E5DFD5] bg-white px-4 py-2 text-sm outline-none focus:border-[#E05638]" />
            <button onClick={ouvrirAcces} disabled={chargement} data-testid="deblocage-ouvrir-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A5C36] px-5 py-2 text-sm font-semibold text-white hover:bg-[#084027] disabled:opacity-50">
              <Unlock size={14} /> Ouvrir l'accès maintenant
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
